import React from 'react';
import { ShowcaseSection, ShowcaseItem } from '../ShowcaseItem';

const COLOR_GROUPS = [
  {
    title: 'Primary',
    colors: [
      { name: 'dark', var: '--color-primary-dark' },
      { name: 'default', var: '--color-primary' },
      { name: 'light', var: '--color-primary-light' },
      { name: 'lighter', var: '--color-primary-lighter' },
      { name: 'lightest', var: '--color-primary-lightest' }
    ]
  },
  {
    title: 'Secondary',
    colors: [
      { name: 'dark', var: '--color-secondary-dark' },
      { name: 'default', var: '--color-secondary' },
      { name: 'light', var: '--color-secondary-light' },
      { name: 'lighter', var: '--color-secondary-lighter' },
      { name: 'lightest', var: '--color-secondary-lightest' }
    ]
  },
  {
    title: 'Semantic',
    colors: [
      { name: 'third', var: '--color-third' },
      { name: 'success', var: '--color-success' },
      { name: 'danger', var: '--color-danger' },
      { name: 'warning', var: '--color-warning' }
    ]
  },
  {
    title: 'Neutral',
    colors: [
      { name: 'bright', var: '--color-neutral-bright' },
      { name: 'lightest', var: '--color-neutral-lightest' },
      { name: 'lighter', var: '--color-neutral-lighter' },
      { name: 'light', var: '--color-neutral-light' },
      { name: 'default', var: '--color-neutral' },
      { name: 'dark', var: '--color-neutral-dark' }
    ]
  }
];

function ColorSwatch({ name, cssVar }) {
  return (
    <div className="flex flex-col items-center gap-1">
      <div
        className="h-16 w-[120px] rounded-lg border border-neutral-200 shadow-card"
        style={{ backgroundColor: `var(${cssVar})` }}
      />
      <span className="text-xs font-bold text-primary-dark">{name}</span>
      <code className="text-[10px] text-neutral-600">{cssVar}</code>
    </div>
  );
}

export function TokensSection() {
  return (
    <ShowcaseSection
      id="tokens"
      title="Tokens"
      description="Cores, tipografia, spacing, radius, sombras, z-index"
    >
      <ShowcaseItem
        label="Cores"
        description="Paleta themeavel via CSS custom properties"
      >
        {COLOR_GROUPS.map(group => (
          <div
            key={group.title}
            className="mb-6 last:mb-0"
          >
            <h4 className="mb-3 text-sm font-bold text-primary-dark">{group.title}</h4>
            <div className="flex flex-wrap gap-4">
              {group.colors.map(color => (
                <ColorSwatch
                  key={color.var}
                  name={color.name}
                  cssVar={color.var}
                />
              ))}
            </div>
          </div>
        ))}
      </ShowcaseItem>

      <ShowcaseItem
        label="Tipografia"
        description="Nunito (headings/body) + Nunito Sans (buttons)"
      >
        <div className="space-y-3">
          {[
            { tag: 'h1', class: 'text-3xl font-extrabold', label: '30px / 800', el: 'h1' },
            { tag: 'h2', class: 'text-2xl font-extrabold', label: '24px / 800', el: 'h2' },
            { tag: 'h3', class: 'text-xl font-extrabold', label: '20px / 800', el: 'h3' },
            { tag: 'h4', class: 'text-lg font-bold', label: '18px / 700', el: 'h4' },
            { tag: 'h5', class: 'text-base font-bold', label: '16px / 700', el: 'h5' },
            { tag: 'h6', class: 'text-sm font-bold', label: '14px / 700', el: 'h6' }
          ].map(({ tag, class: cls, label, el: El }) => (
            <div
              key={tag}
              className="rounded-lg bg-white p-4"
            >
              <code className="mb-1 block text-xs text-neutral-500">
                {tag} — {cls} ({label})
              </code>
              <El className={cls + ' m-0 leading-tight text-primary'}>O rapido cao marrom pula sobre o texto preguicoso</El>
            </div>
          ))}
          <div className="rounded-lg bg-white p-4">
            <code className="mb-1 block text-xs text-neutral-500">body — Nunito 300 / 14px / 1.5</code>
            <p className="m-0">Corpo de texto padrao da plataforma. A fonte Nunito com peso light oferece legibilidade confortavel.</p>
          </div>
        </div>
      </ShowcaseItem>

      <ShowcaseItem
        label="Spacing"
        description="Escala base 4px"
      >
        <div className="space-y-2">
          {[
            { token: '1', value: '4px', width: 'w-4' },
            { token: '2', value: '8px', width: 'w-8' },
            { token: '3', value: '16px', width: 'w-16' },
            { token: '4', value: '24px', width: 'w-24' },
            { token: '5', value: '48px', width: 'w-48' }
          ].map(space => (
            <div
              key={space.token}
              className="flex items-center gap-4"
            >
              <code className="w-20 text-xs text-neutral-600">
                {space.token} — {space.value}
              </code>
              <div className={`h-6 rounded border border-primary-lighter bg-primary-lightest ${space.width}`} />
            </div>
          ))}
        </div>
      </ShowcaseItem>

      <ShowcaseItem label="Border Radius">
        <div className="flex flex-wrap gap-6">
          {[
            { name: 'sm (4px)', className: 'rounded-sm' },
            { name: 'md (6px)', className: 'rounded-md' },
            { name: 'lg (8px)', className: 'rounded-lg' },
            { name: '10px', className: 'rounded-10' },
            { name: 'full', className: 'rounded-full' }
          ].map(radius => (
            <div
              key={radius.name}
              className={`flex h-20 w-20 items-center justify-center border-2 border-primary bg-primary-lightest ${radius.className}`}
            >
              <code className="text-[10px] text-primary-dark">{radius.name}</code>
            </div>
          ))}
        </div>
      </ShowcaseItem>

      <ShowcaseItem
        label="Sombras"
        description="5 niveis de elevacao"
      >
        <div className="flex flex-wrap gap-6">
          {[
            { name: 'card', className: 'shadow-card' },
            { name: 'card-hover', className: 'shadow-card-hover' },
            { name: 'dropshadow', className: 'shadow-dropshadow' },
            { name: 'dropshadow-md', className: 'shadow-dropshadow-md' },
            { name: 'dropshadow-lg', className: 'shadow-dropshadow-lg' }
          ].map(shadow => (
            <div
              key={shadow.name}
              className={`flex h-20 w-36 items-center justify-center rounded-lg bg-white ${shadow.className}`}
            >
              <code className="text-xs text-neutral-600">{shadow.name}</code>
            </div>
          ))}
        </div>
      </ShowcaseItem>

      <ShowcaseItem
        label="Z-Index"
        description="6 camadas de empilhamento"
      >
        <div className="flex items-end gap-2">
          {[
            { name: 'dropdown', value: 10, height: 40 },
            { name: 'sticky', value: 20, height: 60 },
            { name: 'overlay', value: 50, height: 90 },
            { name: 'modal', value: 100, height: 130 },
            { name: 'popover', value: 200, height: 170 },
            { name: 'toast', value: 300, height: 210 }
          ].map(layer => (
            <div
              key={layer.name}
              className="flex w-20 flex-col items-center justify-end rounded-t-md bg-primary px-1 py-2 text-white"
              style={{ height: layer.height }}
            >
              <span className="text-base font-bold">{layer.value}</span>
              <span className="text-[10px]">{layer.name}</span>
            </div>
          ))}
        </div>
      </ShowcaseItem>
    </ShowcaseSection>
  );
}
